import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Clock, Shield, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials } from '../data/mock';

const Home = ({ onChatOpen }) => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                  Compassionate Palliative Care
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Where Comfort <span className="text-blue-600">Meets</span> Care
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Providing holistic palliative care and pain management for cancer and non-cancer terminal illnesses. 
                We support patients and families through stroke, brain injury, dementia, and life-limiting conditions with compassion and dignity.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                    Book a Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-full transition-all">
                    Our Services
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_palliative-portal/artifacts/p62futrx_ChatGPT%20Image%20Apr%205%2C%202026%2C%2010_46_28%20PM.png" 
                  alt="Compassionate care" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <Heart className="text-blue-600" size={24} fill="currentColor" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">24/7</p>
                    <p className="text-sm text-gray-600">Support Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Dard Satya?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine medical expertise with genuine compassion to provide the best possible care
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Heart, title: "Compassionate Care", desc: "Every patient receives personalized attention with dignity and respect" },
              { icon: Users, title: "Expert Team", desc: "Specialized professionals in palliative care and pain management" },
              { 
                icon: Clock, 
                title: "24/7 Availability", 
                subtitle: "CLICK HERE FOR LIVE CHAT",
                desc: "Round-the-clock live chat support for urgent concerns and guidance", 
                isClickable: true 
              },
              { icon: Shield, title: "Holistic Approach", desc: "Addressing physical, emotional, and spiritual needs" }
            ].map((item, index) => (
              <Card 
                key={index} 
                className={`border-none shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 bg-gradient-to-br from-white to-blue-50 ${
                  item.isClickable ? 'cursor-pointer hover:border-4 hover:border-blue-500 ring-4 ring-blue-400 animate-pulse hover:scale-105' : ''
                }`}
                onClick={item.isClickable ? onChatOpen : undefined}
              >
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    item.isClickable ? 'bg-blue-600 shadow-2xl shadow-blue-400 animate-bounce' : 'bg-blue-100'
                  }`}>
                    <item.icon className={item.isClickable ? 'text-white' : 'text-blue-600'} size={28} />
                  </div>
                  <h3 className={`text-xl font-semibold text-gray-900 mb-2 ${item.isClickable ? 'text-2xl' : ''}`}>
                    {item.isClickable ? (
                      <>
                        <span className="block text-3xl font-bold text-blue-600 mb-1">24/7</span>
                        <span className="block text-lg">{item.title}</span>
                      </>
                    ) : (
                      item.title
                    )}
                  </h3>
                  {item.isClickable && (
                    <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-2 animate-pulse inline-block">
                      🟢 LIVE CHAT ACTIVE
                    </div>
                  )}
                  {item.subtitle && (
                    <p className="text-blue-600 font-extrabold text-base mb-2 animate-bounce">
                      👆 {item.subtitle}
                    </p>
                  )}
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive palliative care for various conditions
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service) => (
              <Card key={service.id} className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <Link to={`/services/${service.id}`}>
                    <Button variant="link" className="text-blue-600 hover:text-blue-700 p-0">
                      Learn More <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Families Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hear from those we've had the privilege to serve
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-blue-500" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-4">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.relation}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Provide Comfort for Your Loved One?</h2>
            <p className="text-xl mb-8 text-blue-50">
              Let us help you navigate this journey with compassion, expertise, and unwavering support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-full shadow-lg">
                  Schedule Consultation
                </Button>
              </Link>
              <a href="tel:9123647588">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full">
                  Call: 9123647588
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
